<template>
  <div class="pc-article">
    <div class="pc-article__body">
      <h3>{{ $t('configurator.headings.printable_article') }}</h3>
    </div>
    <div v-if="showMissingWarning" class="alert alert-warning" role="alert">
      {{ $t('configurator.notices.warnings.article_removed')}}
    </div>
    <div class="pc-article__parent">
      <div>
        <img :src="previewImage" alt="" class="pc-article__image">
      </div>
      <div class="pc-article__body">
        <span class="pc-article__title">{{ name }}</span>
        <article class="text-break">{{ description }}</article>
        <b>{{ $t('configurator.labels.count') }}: </b>
        <span v-if="unitIsPc">{{ count }}x</span>
        <span v-if="unitIsSqm">{{ $filters.formatLength(article.amount) }}m<sup>2</sup></span>
      </div>
    </div>
    <div class="pc-article__body">
      <hr>
      <h4 class="border--b-0">{{ $t('configurator.headings.print_configuration') }}</h4>
      <table class="table">
        <thead>
          <th>{{ $t('configurator.tables.columns.surface') }}</th>
          <th>{{ $t('configurator.tables.columns.print_article') }}</th>
          <th>{{ $t('configurator.tables.columns.price') }}</th>
          <th>{{ $t('configurator.tables.columns.graphic_file') }}</th>
          <th></th>
        </thead>
        <tbody>
          <template v-for="groupIndex in count" :key="groupIndex" v-if="article.unit !== 'sqm'">
            <print-cloud-group
              :index="groupIndex"
              :article="article"
              @show="showArticleModal">
            </print-cloud-group>
          </template>

          <template v-else>
            <print-cloud-group
              :index="1"
              :article="article"
              @show="showArticleModal">
            </print-cloud-group>
          </template>
        </tbody>
      </table>
    </div>
  </div>
  <article-modal v-if="articleModalActive"
    :article="selectedArticle"
    @close="hideArticleModal">
  </article-modal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useMainStore } from '@/store'
import { ARTICLE_DEFAULT_IMAGE, UNIT_PC, UNIT_SQM } from '@/constants'

const store = useMainStore()
// define props
const props = defineProps({
  article: {
    type: Object,
    required: true
  }
})
// define reactive props
const articleModalActive = ref(false)
const selectedArticle = ref(null)
// define computed props
const locale = computed(() => store.settings.locale)

const inEnglish = computed(() => {
  return locale.value === 'en'
})

const baseArticle = computed(() => {
  return props.article.article
})

const hasImage = computed(() => {
  if ('images' in baseArticle.value && Array.isArray(baseArticle.value.images)) {
    return baseArticle.value.images.length > 0 
  }

  return false
})

const previewImage = computed(() => {
  if (!hasImage.value) {
    return ARTICLE_DEFAULT_IMAGE
  }

  return '/images/articles/' + baseArticle.value.images[0].path;
})

const name = computed(() => {
  return (inEnglish.value) ? baseArticle.value.name_en : baseArticle.value.name
})

const description = computed(() => {
  return (inEnglish.value) ? baseArticle.value.description_en : baseArticle.value.description
})

const count = computed(() => {
  return parseInt(props.article.amount)
})

const unitIsPc = computed(() => {
  return props.article.unit === UNIT_PC
})

const unitIsSqm = computed(() => {
  return props.article.unit === UNIT_SQM
})

const showMissingWarning = computed(() => {
  return !store.availability.available_article_ids.includes(props.article.article_id)
})
// define methods
const showArticleModal = (article) => {
  selectedArticle.value = article.article
  articleModalActive.value = true
}

const hideArticleModal = () => {
  articleModalActive.value = false
  selectedArticle.value = null
}
</script>